import * as THREE from 'three'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js'
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js'
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js'
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js'

// Path to hero model — update when GLB is provided
const HERO_MODEL_PATH = '/assets/HeroModel/hero.glb'

class HeroViewport {
  private container: HTMLElement
  private scene: THREE.Scene
  private camera: THREE.PerspectiveCamera
  private renderer: THREE.WebGLRenderer
  private composer: EffectComposer
  private bloomPass: UnrealBloomPass
  private animationId: number | null = null
  private isVisible = true

  // Auto-rotation state (no OrbitControls — hero is purely decorative)
  private orbitTarget = new THREE.Vector3()
  private orbitRadius = 4
  private orbitAngle = 25 * (Math.PI / 180) // start 25° right
  private orbitDirection = -1 // -1 = rotating left, 1 = rotating right
  private readonly pingPongMin = -25 * (Math.PI / 180)
  private readonly pingPongMax = 25 * (Math.PI / 180)

  constructor(containerSelector: string) {
    const container = document.querySelector(containerSelector) as HTMLElement
    if (!container) {
      throw new Error(`Container not found: ${containerSelector}`)
    }
    this.container = container

    // Scene — no background for transparency
    this.scene = new THREE.Scene()

    // Camera
    this.camera = new THREE.PerspectiveCamera(45, 1, 0.1, 1000)
    this.camera.position.set(0, 0, 4)

    // Renderer — alpha for transparent background
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
    this.renderer.setClearColor(0x000000, 0)
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping
    this.renderer.toneMappingExposure = 1.0
    // Cap at 1x DPR — viewport is very large, bloom cost scales with pixel count
    this.renderer.setPixelRatio(1)

    // Post-processing (initialized in init, assigned here to satisfy TS)
    this.bloomPass = null!
    this.composer = null!

    this.init()
  }

  private init(): void {
    // Renderer
    this.updateSize()
    this.container.appendChild(this.renderer.domElement)
    this.renderer.domElement.style.opacity = '0'
    this.renderer.domElement.style.transition = 'opacity 0.4s ease'

    // Environment
    this.setupEnvironment()

    // Post-processing
    this.setupPostProcessing()

    // Observers
    this.setupResizeObserver()
    this.setupVisibilityObserver()

    // Load model
    this.loadModel()

    // Start render loop
    this.animate()
  }

  private setupEnvironment(): void {
    const pmremGenerator = new THREE.PMREMGenerator(this.renderer)
    pmremGenerator.compileEquirectangularShader()

    const roomEnv = new RoomEnvironment()
    const envMap = pmremGenerator.fromScene(roomEnv).texture

    this.scene.environment = envMap
    pmremGenerator.dispose()
  }

  private setupPostProcessing(): void {
    const width = this.container.clientWidth
    const height = this.container.clientHeight

    // Use a render target with alpha for transparency
    const renderTarget = new THREE.WebGLRenderTarget(width, height, {
      type: THREE.HalfFloatType,
      format: THREE.RGBAFormat
    })
    this.composer = new EffectComposer(this.renderer, renderTarget)

    // Pass 1: Render scene
    const renderPass = new RenderPass(this.scene, this.camera)
    renderPass.clearAlpha = 0
    this.composer.addPass(renderPass)

    // Pass 2: Bloom — subtle glow on specular highlights
    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(width, height),
      0.5,   // strength (reduced by ~1/3 from 0.8)
      0.3,   // radius
      0.85   // threshold
    )
    this.composer.addPass(this.bloomPass)

    // Pass 3: Output — applies tone mapping + sRGB conversion
    const outputPass = new OutputPass()
    this.composer.addPass(outputPass)

    this.composer.setSize(width, height)
  }

  private loadModel(): void {
    const loader = new GLTFLoader()
    const dracoLoader = new DRACOLoader()
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/')
    loader.setDRACOLoader(dracoLoader)

    loader.load(
      HERO_MODEL_PATH,
      (gltf) => {
        const model = gltf.scene

        // Center and scale
        const box = new THREE.Box3().setFromObject(model)
        const center = box.getCenter(new THREE.Vector3())
        const size = box.getSize(new THREE.Vector3())
        const maxDim = Math.max(size.x, size.y, size.z)
        const scale = 1.8 / maxDim

        model.scale.setScalar(scale)
        model.position.sub(center.multiplyScalar(scale))

        this.scene.add(model)
        this.fitCameraToObject(model)

        // Reveal
        this.renderer.domElement.style.opacity = '1'
        const loading = this.container.querySelector('.viewport-loading') as HTMLElement
        if (loading) {
          loading.style.transition = 'opacity 0.3s ease'
          loading.style.opacity = '0'
          setTimeout(() => loading.remove(), 300)
        }
      },
      undefined,
      (error) => {
        console.warn('Failed to load hero model:', error)
      }
    )
  }

  private fitCameraToObject(object: THREE.Object3D): void {
    const box = new THREE.Box3().setFromObject(object)
    const size = box.getSize(new THREE.Vector3())
    const center = box.getCenter(new THREE.Vector3())

    const maxDim = Math.max(size.x, size.y, size.z)
    const fov = this.camera.fov * (Math.PI / 180)
    let cameraDistance = (maxDim / 2) / Math.tan(fov / 2)
    cameraDistance *= 1.4

    this.orbitTarget.copy(center)
    this.orbitRadius = cameraDistance

    // Start 25 degrees to the right — dwarf faces left toward text
    this.orbitAngle = 25 * (Math.PI / 180)
    this.updateCameraFromOrbit()
  }

  private updateCameraFromOrbit(): void {
    this.camera.position.set(
      this.orbitTarget.x + Math.sin(this.orbitAngle) * this.orbitRadius,
      this.orbitTarget.y,
      this.orbitTarget.z + Math.cos(this.orbitAngle) * this.orbitRadius
    )
    this.camera.lookAt(this.orbitTarget)
  }

  private setupResizeObserver(): void {
    const resizeObserver = new ResizeObserver(() => {
      this.updateSize()
    })
    resizeObserver.observe(this.container)
  }

  private setupVisibilityObserver(): void {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          this.isVisible = entry.isIntersecting
        })
      },
      { threshold: 0.1 }
    )
    observer.observe(this.container)
  }

  private updateSize(): void {
    const width = this.container.clientWidth
    const height = this.container.clientHeight

    this.camera.aspect = width / height
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(width, height)

    if (this.composer) {
      this.composer.setSize(width, height)
      this.bloomPass.resolution.set(width, height)
    }
  }

  private animate = (): void => {
    this.animationId = requestAnimationFrame(this.animate)

    if (this.isVisible) {
      // Ping-pong auto-rotation between ±25 degrees
      // OrbitControls autoRotateSpeed of 0.2 ≈ ~0.001 rad/frame at 60fps
      const step = 0.001 * this.orbitDirection
      this.orbitAngle += step

      if (this.orbitAngle <= this.pingPongMin) {
        this.orbitDirection = 1
      } else if (this.orbitAngle >= this.pingPongMax) {
        this.orbitDirection = -1
      }

      this.updateCameraFromOrbit()
      this.composer.render()
    }
  }

  public dispose(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
    }
    this.composer.dispose()
    this.renderer.dispose()
  }
}

export function initHeroViewport(): void {
  // Skip 3D viewport on mobile — use video fallback instead
  if (window.innerWidth < 1024) return

  const container = document.getElementById('hero-viewport')
  if (container) {
    new HeroViewport('#hero-viewport')
  }
}
